import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { SkeletonLoader } from "@via/components/loading/loading.tsx";
import Table, { TableSize } from "@via/components/table/table.tsx";
import SelectComponent from "@via/components/select/select.tsx";
import { GenericError } from "@via/components/error.tsx";
import { useAuth } from "@via/hooks/useAuth.tsx";
import {
  formatCurrency,
  mergeRunEstimationsByDate,
} from "@via/pages/organization/helpers.tsx";
import UsageBarCharts, {
  UsageMetricOptions,
} from "@via/pages/organization/usage/usage-bar-charts.tsx";
import {
  getRunStatusDisplayText,
  RunAnalyticsFilterMode,
  type RunAnalyticsEstimation,
} from "common";
import { useMemo, useState, useEffect } from "react";
import { downloadCSV, formatDateString } from "@via/utils/helpers.tsx";
import { USER_ROLES } from "@via/utils/constants.tsx";
import { useRunAnalyticsEstimations } from "@via/pages/projects/runs/runs.api.tsx";
import { Link } from "react-router-dom";
import { useDebounceCallback } from "usehooks-ts";
import type { SelectOption } from "@via/utils/interfaces.tsx";
import { getRunStatusLabel } from "@via/pages/projects/runs/utils/helpers.tsx";
import RunPipelineLink from "@via/pages/projects/runs/run-pipeline-link.tsx";

const usageCurrency = "usd";
const columnHelper = createColumnHelper<RunAnalyticsEstimation>();

type NumberOption = { label: string; value: number };
type ModeOption = { label: string; value: RunAnalyticsFilterMode };

const TIMEFRAME_OPTIONS: NumberOption[] = [
  { label: "All time", value: 0 },
  { label: "Last 7 days", value: 7 },
  { label: "Last 30 days", value: 30 },
  { label: "Last 90 days", value: 90 },
  { label: "Last 180 days", value: 180 },
  { label: "Last 365 days", value: 365 },
];

const MODE_OPTIONS: ModeOption[] = [
  { label: "All runs", value: RunAnalyticsFilterMode.AllRuns },
  {
    label: "All versions of a pipeline",
    value: RunAnalyticsFilterMode.PipelineName,
  },
  {
    label: "Specific pipeline versions",
    value: RunAnalyticsFilterMode.PipelineVersion,
  },
];

const CSV_COLUMNS = [
  { accessorKey: "runId" },
  { accessorKey: "runName" },
  { accessorKey: "pipelineName" },
  { accessorKey: "pipelineVersion" },
  { accessorKey: "owner" },
  { accessorKey: "runStatus" },
  { accessorKey: "runEnvironment" },
  { accessorKey: "totalCost" },
  { accessorKey: "totalCpuTime" },
  { accessorKey: "totalMemoryTime" },
  { accessorKey: "totalDiskTime" },
  { accessorKey: "runEndDate" },
];

const isSelectOptionArray = (
  val: SelectOption | readonly SelectOption[]
): val is readonly SelectOption[] => Array.isArray(val);

const toSingleOption = (
  val: SelectOption | readonly SelectOption[]
): SelectOption | null =>
  isSelectOptionArray(val) ? (val[0] ?? null) : (val ?? null);

type PipelineSelectItem = {
  id: number;
  gid: number | null;
  name: string;
  version: string;
};

export default function RunAnalyticsTab({
  initialPipeline,
}: {
  initialPipeline?: PipelineSelectItem;
}): JSX.Element {
  const { user } = useAuth();
  const isAdmin = user?.userRole === USER_ROLES.ADMIN;
  const isCostPredictionAllowed = !!user?.allowedActions?.allowCostPrediction;

  const [days, setDays] = useState<number>(30);

  const [filterMode, setFilterMode] = useState<RunAnalyticsFilterMode>(
    RunAnalyticsFilterMode.AllRuns
  );

  const [debouncedSearchKey, setDebouncedSearchKey] = useState<string>("");
  const debounced = useDebounceCallback((value: string) => {
    setDebouncedSearchKey(value);
  }, 300);

  const [selectedPipelineGid, setSelectedPipelineGid] = useState<number | null>(
    null
  );
  const [selectedPipelineVersions, setSelectedPipelineVersions] = useState<
    NumberOption[]
  >([]);

  useEffect(() => {
    if (!initialPipeline) return;
    setSelectedPipelineGid(initialPipeline.gid);
    setSelectedPipelineVersions([
      {
        label: `${initialPipeline.name} (${initialPipeline.version})`,
        value: initialPipeline.id,
      },
    ]);
  }, [
    initialPipeline?.id,
    initialPipeline?.gid,
    initialPipeline?.name,
    initialPipeline?.version,
  ]);

  const allRunsQuery = useMemo(
    () => ({
      days,
      filterMode: RunAnalyticsFilterMode.AllRuns,
    }),
    [days]
  );

  const {
    data: allRunsEstimations = [],
    isLoading: isAllRunsLoading,
    isError: isAllRunsError,
  } = useRunAnalyticsEstimations(allRunsQuery, true);

  const filteredQuery = useMemo(() => {
    if (filterMode === RunAnalyticsFilterMode.PipelineName) {
      return {
        days,
        filterMode,
        pipelineGid: selectedPipelineGid ?? undefined,
      };
    }
    if (filterMode === RunAnalyticsFilterMode.PipelineVersion) {
      return {
        days,
        filterMode,
        pipelineIds: selectedPipelineVersions.map(v => v.value).join(","),
      };
    }
    return allRunsQuery;
  }, [
    days,
    filterMode,
    selectedPipelineGid,
    selectedPipelineVersions,
    allRunsQuery,
  ]);

  const shouldFetchFiltered =
    filterMode === RunAnalyticsFilterMode.PipelineName ||
    filterMode === RunAnalyticsFilterMode.PipelineVersion;

  const {
    data: filteredEstimations = [],
    isLoading: isFilteredLoading,
    isError: isFilteredError,
  } = useRunAnalyticsEstimations(filteredQuery, shouldFetchFiltered);

  const activeEstimations =
    filterMode === RunAnalyticsFilterMode.AllRuns
      ? allRunsEstimations
      : filteredEstimations;

  const visibleEstimations = useMemo(() => {
    if (filterMode !== RunAnalyticsFilterMode.AllRuns) return activeEstimations;
    const q = debouncedSearchKey.trim().toLowerCase();
    if (!q) return activeEstimations;

    const includes = (v: unknown) =>
      v != null && String(v).toLowerCase().includes(q);

    return activeEstimations.filter(row => {
      return (
        includes(row.runId) ||
        includes(row.name) ||
        includes(row.pipelineName) ||
        includes(row.pipelineVersion) ||
        includes(row.pipelineId) ||
        includes(row.pipelineGid) ||
        includes(row.username) ||
        includes(row.runStatus) ||
        includes(row.runEnvironmentName)
      );
    });
  }, [activeEstimations, debouncedSearchKey, filterMode]);

  const isLoading =
    filterMode === RunAnalyticsFilterMode.AllRuns
      ? isAllRunsLoading
      : isFilteredLoading;

  const isError =
    filterMode === RunAnalyticsFilterMode.AllRuns
      ? isAllRunsError
      : isFilteredError;

  const formattedRunEstimations = useMemo(
    () => mergeRunEstimationsByDate(visibleEstimations),
    [visibleEstimations]
  );

  const csvRows = useMemo(() => {
    return visibleEstimations.map(row => {
      const canShowCost =
        isCostPredictionAllowed && (isAdmin || row.userId === user?.id);

      const endDate =
        row.runEndDate instanceof Date
          ? row.runEndDate.toISOString()
          : row.runEndDate
            ? new Date(row.runEndDate).toISOString()
            : "";

      return {
        runId: row.runId,
        runName: row.name,
        pipelineName: row.pipelineName,
        pipelineVersion: row.pipelineVersion,
        owner: row.username,
        runStatus: getRunStatusDisplayText(row.runStatus ?? undefined),
        runEnvironment: row.runEnvironmentName ?? "",
        totalCost: canShowCost ? row.totalCost : "",
        totalCpuTime: row.totalCpuTime,
        totalMemoryTime: row.totalMemoryTime,
        totalDiskTime: row.totalDiskTime,
        runEndDate: endDate,
      };
    });
  }, [visibleEstimations, isAdmin, isCostPredictionAllowed, user?.id]);

  const pipelineNameOptions = useMemo<NumberOption[]>(() => {
    // Group by pipelineGid (preferred), fallback to pipelineName if gid missing
    const map = new Map<string, { gid: number | null; name: string }>();
    for (const row of allRunsEstimations) {
      const key =
        row.pipelineGid !== null
          ? `gid:${row.pipelineGid}`
          : `name:${row.pipelineName}`;
      if (!map.has(key)) {
        map.set(key, { gid: row.pipelineGid ?? null, name: row.pipelineName });
      }
    }
    return Array.from(map.values())
      .filter(v => v.gid !== null)
      .map(v => ({ label: v.name, value: v.gid as number }));
  }, [allRunsEstimations]);

  const pipelineVersionOptions = useMemo<NumberOption[]>(() => {
    const map = new Map<number, { id: number; label: string }>();
    for (const row of allRunsEstimations) {
      if (!map.has(row.pipelineId)) {
        map.set(row.pipelineId, {
          id: row.pipelineId,
          label: `${row.pipelineName} (${row.pipelineVersion})`,
        });
      }
    }
    return Array.from(map.values()).map(v => ({ label: v.label, value: v.id }));
  }, [allRunsEstimations]);

  const metricOptions = useMemo(() => {
    if (isCostPredictionAllowed) return UsageMetricOptions;
    return UsageMetricOptions.filter(opt => opt.value !== "totalCost");
  }, [isCostPredictionAllowed]);

  const columns = useMemo(() => {
    const cols = [
      columnHelper.accessor("runId", {
        cell: info => (
          <Link
            to={`/runs/${info.getValue()}`}
            target="_blank"
            rel="noreferrer"
            className="via-link truncate">
            {info.getValue()}
          </Link>
        ),
        header: () => <span>Run ID</span>,
      }),
      columnHelper.accessor("pipelineName", {
        cell: info => (
          <RunPipelineLink
            pipelineId={info.row.original.pipelineId}
            pipelineName={`${info.getValue()} (${info.row.original.pipelineVersion})`}
            subjectType={info.row.original.pipelineSubjectType}
            openInNewTab
          />
        ),
        header: () => <span>Pipeline</span>,
      }),
      columnHelper.accessor("name", {
        cell: info => (
          <Link
            to={`/runs/${info.row.original.runId}`}
            target="_blank"
            rel="noreferrer"
            className="via-link truncate">
            {info.getValue()}
          </Link>
        ),
        header: () => <span>Run</span>,
      }),
      columnHelper.accessor(row => row.runStatus ?? null, {
        id: "runStatus",
        cell: ({ row }) => {
          const v = row.original.runStatus;
          return (
            <div className="flex items-center gap-2">
              {v ? getRunStatusLabel(v) : "—"}
            </div>
          );
        },
        header: () => <span>Status</span>,
        enableSorting: false,
      }),
      ...(isCostPredictionAllowed
        ? [
            columnHelper.accessor("totalCost", {
              cell: info => {
                const row = info.row.original;
                const canShow = isAdmin || row.userId === user?.id;
                return (
                  <div>
                    {canShow
                      ? formatCurrency(info.getValue(), usageCurrency, 2)
                      : "—"}
                  </div>
                );
              },
              header: () => <span>Total Cost</span>,
            }),
          ]
        : []),
      columnHelper.accessor("totalCpuTime", {
        cell: info => <div>{info.getValue()}</div>,
        header: () => <span>Total Cpu Time (CPU⋅min)</span>,
      }),
      columnHelper.accessor("totalMemoryTime", {
        cell: info => <div>{info.getValue()}</div>,
        header: () => <span>Total Memory Time (GB⋅min)</span>,
      }),
      columnHelper.accessor("totalDiskTime", {
        cell: info => <div>{info.getValue()}</div>,
        header: () => <span>Total Disk Time (GB⋅min)</span>,
      }),
      columnHelper.accessor("username", {
        cell: info => <div>{info.getValue()}</div>,
        header: () => <span>Owner</span>,
      }),
      columnHelper.accessor(row => row.runEnvironmentName ?? null, {
        id: "runEnvironmentName",
        cell: info => {
          const v = info.getValue();
          return <div>{v ? String(v) : "—"}</div>;
        },
        header: () => <span>Run Environment</span>,
        enableSorting: false,
      }),
      columnHelper.accessor("runEndDate", {
        cell: info => formatDateString(info.getValue(), "LL"),
        header: () => <span>Date</span>,
        enableSorting: false,
      }),
    ] as Array<ColumnDef<RunAnalyticsEstimation>>;
    return cols;
  }, [isCostPredictionAllowed, isAdmin, user?.id]);

  return (
    <div className="my-5 w-full">
      <div className="mb-4 flex flex-wrap items-end gap-4">
        <div className="flex flex-1 flex-wrap items-end gap-4 min-w-0">
          <div className="min-w-[220px]">
            <label className="mb-2 block text-sm font-medium leading-6 text-gray-900">
              Date range
            </label>
            <SelectComponent
              name="run-analytics-timeframe"
              enableClear={false}
              options={TIMEFRAME_OPTIONS}
              value={TIMEFRAME_OPTIONS.find(o => o.value === days)}
              onChange={val => {
                const opt = toSingleOption(val);
                const nextDays =
                  typeof opt?.value === "number" ? opt.value : null;
                // 0 means "All time"
                if (
                  nextDays !== null &&
                  Number.isInteger(nextDays) &&
                  nextDays >= 0
                ) {
                  setDays(nextDays);
                }
              }}
            />
          </div>

          <div className="min-w-[220px]">
            <label className="mb-2 block text-sm font-medium leading-6 text-gray-900">
              Group by / Filter
            </label>
            <SelectComponent
              name="run-analytics-filter-mode"
              enableClear={false}
              options={MODE_OPTIONS}
              value={MODE_OPTIONS.find(o => o.value === filterMode)}
              onChange={val => {
                const opt = toSingleOption(val);
                const v = opt?.value;
                if (
                  typeof v === "string" &&
                  (Object.values(RunAnalyticsFilterMode) as string[]).includes(
                    v
                  )
                ) {
                  setFilterMode(v as RunAnalyticsFilterMode);
                }
              }}
            />
          </div>

          {filterMode === RunAnalyticsFilterMode.AllRuns ? (
            <div className="min-w-[320px] flex-grow">
              <label className="mb-2 block text-sm font-medium leading-6 text-gray-900">
                Search runs
              </label>
              <input
                type="text"
                placeholder="Run name, pipeline, owner, id, status…"
                className="w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                defaultValue={debouncedSearchKey}
                onChange={e => debounced(e.target.value)}
              />
            </div>
          ) : null}

          {filterMode === RunAnalyticsFilterMode.PipelineName ? (
            <div className="min-w-[320px]">
              <label className="mb-2 block text-sm font-medium leading-6 text-gray-900">
                Pipeline Name
              </label>
              <SelectComponent
                name="run-analytics-pipeline-name"
                enableClear={false}
                options={pipelineNameOptions}
                value={
                  selectedPipelineGid
                    ? (pipelineNameOptions.find(
                        o => o.value === selectedPipelineGid
                      ) ?? null)
                    : null
                }
                onChange={val => {
                  const opt = toSingleOption(val);
                  setSelectedPipelineGid(
                    typeof opt?.value === "number" ? opt.value : null
                  );
                }}
                isLoading={isAllRunsLoading}
              />
            </div>
          ) : filterMode === RunAnalyticsFilterMode.PipelineVersion ? (
            <div className="min-w-[420px]">
              <label className="mb-2 block text-sm font-medium leading-6 text-gray-900">
                Pipeline Versions
              </label>
              <SelectComponent
                name="run-analytics-pipeline-versions"
                enableClear={false}
                isMulti={true}
                options={pipelineVersionOptions}
                value={selectedPipelineVersions}
                onChange={val => {
                  const arr = Array.isArray(val) ? val : [];
                  const next: NumberOption[] = arr
                    .filter(o => typeof o.value === "number" && o.label != null)
                    .map(o => ({
                      label: String(o.label),
                      value: o.value as number,
                    }));
                  setSelectedPipelineVersions(next);
                }}
                isLoading={isAllRunsLoading}
              />
            </div>
          ) : null}
        </div>

        <div className="ml-auto min-w-[170px] flex justify-end">
          <button
            type="button"
            className="h-[38px] inline-flex items-center justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={!visibleEstimations.length}
            onClick={() => downloadCSV(CSV_COLUMNS, csvRows)}>
            Download CSV
          </button>
        </div>
      </div>

      {isLoading && (
        <div className="grid grid-cols-1 gap-6 py-6 sm:grid-cols-2 lg:grid-cols-3">
          <SkeletonLoader count={12} />
        </div>
      )}

      {!isLoading && isError ? (
        <div className="via-generic-error">
          <GenericError message="Failed to load analytics." />
        </div>
      ) : null}

      {!isLoading && !isError ? (
        <>
          {activeEstimations.length ? (
            <UsageBarCharts
              runEstimations={formattedRunEstimations}
              metricOptions={metricOptions}
              heightPx={400}
            />
          ) : null}
          <div className="my-4 overflow-x-auto">
            <Table
              columns={columns}
              data={visibleEstimations}
              total={visibleEstimations.length}
              size={TableSize.Big}
              emptyMessage="No run analytics found for the selected filters."
              customTableClassName="min-w-[1600px] w-max table-auto [&_th]:whitespace-nowrap [&_td]:whitespace-nowrap [&_td]:break-normal"
            />
          </div>
        </>
      ) : null}
    </div>
  );
}
